import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import LiveSalesRoomPlayer from '../components/LiveSalesRoomPlayer';
import { liveAPI } from '../api/client';
import { Loader2, AlertCircle } from 'lucide-react';

export default function LiveRoomPage({ onAddToCart, onBuyNow }) {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const [activeRooms, setActiveRooms] = useState([]);
    const [roomDetails, setRoomDetails] = useState(null);
    const [error, setError] = useState(null);

    const fetchActiveRoomsList = useCallback(async () => {
        try {
            const rooms = await liveAPI.getActiveRooms();
            const validRooms = Array.isArray(rooms)
                ? rooms.filter(r => !r.status || r.status === 'ACTIVE' || r.status === 'LIVE' || r.status === 'PREVIEW' || r.status === 'MEDIA_CHECKING')
                : [];
            setActiveRooms(validRooms);
            const found = validRooms.find(r => String(r.id) === String(roomId));
            if (found) {
                setRoomDetails(found);
            } else {
                setRoomDetails(prev => prev && String(prev.id) === String(roomId) ? prev : { id: roomId, title: 'LUXVONO Live Showcase', type: 'LIVE' });
            }
            return validRooms;
        } catch (_) {
            setRoomDetails(prev => prev || { id: roomId, title: 'LUXVONO Live Showcase', type: 'LIVE' });
            return [];
        }
    }, [roomId]);

    useEffect(() => {
        fetchActiveRoomsList();
        const interval = setInterval(fetchActiveRoomsList, 10000);
        return () => clearInterval(interval);
    }, [fetchActiveRoomsList]);

    const currentRoomIndex = Math.max(0, activeRooms.findIndex(r => String(r.id) === String(roomId)));

    const handleSwitchRoom = useCallback(async (direction = 'next', notifyCallback = null) => {
        let roomsList = activeRooms;
        if (roomsList.length <= 1) {
            const refreshed = await fetchActiveRoomsList();
            if (refreshed.length > 0) {
                roomsList = refreshed;
            }
        }
        if (roomsList.length <= 1) {
            if (typeof notifyCallback === 'function') {
                notifyCallback('single');
            }
            return false;
        }
        const currIdx = Math.max(0, roomsList.findIndex(r => String(r.id) === String(roomId)));
        const nextIdx = direction === 'prev'
            ? (currIdx - 1 + roomsList.length) % roomsList.length
            : (currIdx + 1) % roomsList.length;
        const targetRoom = roomsList[nextIdx];
        if (targetRoom && String(targetRoom.id) !== String(roomId)) {
            setRoomDetails(targetRoom);
            navigate(`/live/${targetRoom.id}`, { replace: true });
            if (typeof notifyCallback === 'function') {
                notifyCallback('switched', targetRoom, nextIdx, roomsList.length);
            }
            return true;
        }
        return false;
    }, [activeRooms, fetchActiveRoomsList, navigate, roomId]);

    if (error) {
        return (
            <div style={{ minHeight: '100vh', background: '#050508', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                <div style={{ background: '#12131a', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '24px', padding: '32px', maxWidth: '480px', width: '100%', textAlign: 'center', color: '#fff' }}>
                    <AlertCircle style={{ color: '#ef4444', margin: '0 auto 16px', display: 'block' }} size={48} />
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' }}>Room Notice</h2>
                    <p style={{ color: '#9ca3af', marginBottom: '24px', fontSize: '14px' }}>{error}</p>
                    <button
                        onClick={() => navigate('/live')}
                        style={{ background: '#C8A96A', color: '#050508', fontWeight: 'bold', padding: '12px 24px', borderRadius: '12px', border: 'none', cursor: 'pointer' }}
                    >
                        Browse Active Rooms
                    </button>
                </div>
            </div>
        );
    }

    if (!roomDetails) {
        return (
            <div style={{ minHeight: '100vh', background: '#050508', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Loader2 style={{ animation: 'spin 1s linear infinite', color: '#C8A96A' }} size={48} />
            </div>
        );
    }

    return (
        <LiveSalesRoomPlayer
            roomId={roomId}
            roomTitle={roomDetails.title}
            allRooms={activeRooms}
            currentRoomIndex={currentRoomIndex}
            onSwitchRoom={handleSwitchRoom}
            onLeave={() => navigate('/')}
            onAddToCart={onAddToCart}
            onBuyNow={onBuyNow}
        />
    );
}
